import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { HlmSkeleton } from '@lite-workspace/shared/helm-ui/skeleton';

/** Loading shape of a cabinet page: album rows, photo tiles, face rows. */
export type PageSkeletonShape = 'rows' | 'tiles' | 'faces';

/**
 * One loading look per page shape — drawn by the page itself and by the route veil ahead of it.
 * Placeholders on a `bg-bg2` card take `bg-panel3`: the default `--muted` aliases `--bg2` and vanishes there.
 */
@Component({
  selector: 'hlm-page-skeleton',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmSkeleton],
  host: { class: 'block' },
  template: `
    @switch (shape()) {
      @case ('rows') {
        <div class="flex flex-col gap-6.5">
          @for (row of rows; track row) {
            <div class="rounded-[var(--r-l)] border border-line bg-bg2 px-5 pb-5 pt-4.5">
              <div class="mb-4.5 flex items-center gap-3">
                <div class="size-10 overflow-hidden rounded-full">
                  <div hlmSkeleton class="size-full bg-panel3"></div>
                </div>
                <div hlmSkeleton class="h-4 w-40 bg-panel3"></div>
              </div>
              <div class="grid grid-cols-3 gap-2.75 md:grid-cols-7">
                @for (cell of rowThumbs; track cell) {
                  <div hlmSkeleton class="aspect-[400/284] bg-panel3"></div>
                }
              </div>
            </div>
          }
        </div>
      }
      @case ('tiles') {
        <div class="grid grid-cols-3 gap-2 md:grid-cols-5">
          @for (tile of tiles; track tile) {
            <div hlmSkeleton class="aspect-square rounded-[var(--r-control)]"></div>
          }
        </div>
      }
      @case ('faces') {
        <div class="rounded-[var(--r-m)] border border-line bg-bg2 p-4">
          @for (face of faces; track face) {
            <div class="flex items-center gap-4 py-4">
              <div hlmSkeleton class="size-24 shrink-0 rounded-full bg-panel3"></div>
              <div class="flex min-w-0 flex-1 flex-col gap-3">
                <div hlmSkeleton class="h-9 w-full max-w-80 bg-panel3"></div>
                <div hlmSkeleton class="h-9 w-full max-w-80 bg-panel3"></div>
              </div>
            </div>
          }
        </div>
      }
    }
  `,
})
export class HlmPageSkeleton {
  readonly shape = input.required<PageSkeletonShape>();

  protected readonly rows = Array.from({ length: 3 }, (_, index) => index);
  protected readonly rowThumbs = Array.from({ length: 7 }, (_, index) => index);
  protected readonly tiles = Array.from({ length: 20 }, (_, index) => index);
  protected readonly faces = Array.from({ length: 10 }, (_, index) => index);
}
